import { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { Colors, ScorecardConstants } from "constants/constants";
import { handleGetReturnedDataByDriver } from "../requests";
import { StationSelect } from "componentsNew/elements/SelectField";
import { SearchTypeSelect } from "components/elements/SearchTypeSelect";
import { MonthSelect } from "components/elements/MonthSelect";
import { WeekSelect } from "components/elements/WeekSelect";
import { useDateWeekSelector } from "hooks/useDateWeekSelector";
import { useStyles } from "./styles";
import { ScorecardOpenButton } from "components/buttons/ScorecardOpenButton";
import { ScorecardCompareButton } from "components/buttons/ScorecardCompareButton";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { useDispatch } from "react-redux";
import {
  ScorecardExternalSetDate,
  ScorecardSetSelectType,
  ScorecardSetStation,
} from "actions/scorecardActions";
import { DatePickWithArrow } from "components/elements/DatePickWithArrow";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const ReturnedDataByDriver = ({ withModal = true, withCompare = true }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { userData } = useTypeSelectors((state) => state.auth);
  const { externalSettingActive, externalSettings, returnedDataByDriverSettings } =
    useTypeSelectors((state) => state.scoreCardSettings);
  const defaultStationCondition = userData?.station_spots.some(
    (el: { station: { title: string; id: number } }) => el.station.id === 1
  )
    ? 1
    : userData?.station_spots[0]?.station?.id;
  const [loading, setLoading] = useState(false);
  const [compareStation, setCompareStation] = useState(`${defaultStationCondition}`);
  const [requestData, setRequestData] = useState([]);

  const handleChangeStation = (data: string) =>
    !withCompare
      ? setCompareStation(data)
      : dispatch(ScorecardSetStation(data, ScorecardConstants.RETURNED_DATA_BY_DRIVER_STATION));
  const handleChangeSelectType = (select: string) =>
    dispatch(
      ScorecardSetSelectType(select, ScorecardConstants.RETURNED_DATA_BY_DRIVER_SELECT_TYPE)
    );
  const handleChangeWeek = (week: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...returnedDataByDriverSettings?.date, week },
        ScorecardConstants.RETURNED_DATA_BY_DRIVER_DATE
      )
    );
  const handleChangeMonth = (month: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...returnedDataByDriverSettings?.date, month },
        ScorecardConstants.RETURNED_DATA_BY_DRIVER_DATE
      )
    );
  const handleChangeYear = (year: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...returnedDataByDriverSettings?.date, year },
        ScorecardConstants.RETURNED_DATA_BY_DRIVER_DATE
      )
    );

  const handleChangeDayDate = (date: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...returnedDataByDriverSettings?.date, day: date },
        ScorecardConstants.RETURNED_DATA_BY_DRIVER_DATE
      )
    );

  const {
    week,
    month,
    year,
    selectType,
    date,
    onTypeChange,
    dateCondition,
    dateConditionMonth,
    onWeekReduce,
    onWeekIncrease,
    onMonthReduce,
    onMonthIncrease,
    onDateChange,
    onDateChangeByDay,
  } = useDateWeekSelector({
    defaultWeek: returnedDataByDriverSettings?.date?.week,
    defaultMonth: returnedDataByDriverSettings?.date?.month,
    defaultYear: returnedDataByDriverSettings?.date?.year,
    defaultSelectType: returnedDataByDriverSettings?.selectType,
    onSelectTypeSet: handleChangeSelectType,
    onWeekSet: handleChangeWeek,
    onMonthSet: handleChangeMonth,
    onYearSet: handleChangeYear,
    onDateSet: handleChangeDayDate,
  });

  const conditionLocalStation = !withCompare
    ? compareStation
    : returnedDataByDriverSettings?.station;

  useEffect(() => {
    if (
      (conditionLocalStation && (month || week || year || date)) ||
      (externalSettings?.station && externalSettings?.selectType && externalSettings?.date)
    ) {
      const params = externalSettingActive
        ? {
            station: externalSettings?.station,
            ...(externalSettings?.selectType === "month" && {
              month: +externalSettings?.date?.month,
              year: +externalSettings?.date?.year,
            }),
            ...(externalSettings?.selectType === "week" && {
              week: +externalSettings?.date?.week,
              year: +externalSettings?.date?.year,
            }),
            ...(externalSettings?.selectType === "day" && { day: externalSettings?.date?.day }),
          }
        : {
            station: conditionLocalStation,
            ...(selectType === "month" && { month, year: year }),
            ...(selectType === "week" && { week, year: year }),
            ...(selectType === "day" && { day: date }),
          };
      handleGetReturnedDataByDriver(params, setRequestData, setLoading);
    }
  }, [
    returnedDataByDriverSettings?.station,
    year,
    month,
    week,
    date,
    selectType,
    externalSettingActive,
    externalSettings?.station,
    externalSettings?.selectType,
    externalSettings?.date,
    conditionLocalStation,
  ]);

  const labelsData = requestData.filter((el: { name: string }, index) =>
    index < 10 ? el.name : null
  );

  const labels = labelsData.map((el: { name: string }) => el.name);

  const dataChart = {
    labels,
    datasets: [
      {
        label: "UTA",
        data: labelsData.map((el: { uta_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.uta_sum : null
        ),
        backgroundColor: Colors.PASTEL_BLUE,
        barThickness: 15,
        borderRadius: 25,
      },
      {
        label: "UTL",
        data: labelsData.map((el: { utl_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.utl_sum : null
        ),
        backgroundColor: Colors.PASTEL_GREEN,
        barThickness: 15,
        borderRadius: 25,
      },
      {
        label: "NSL",
        data: labelsData.map((el: { nsl_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.nsl_sum : null
        ),
        backgroundColor: Colors.PASTEL_ORANGE,
        barThickness: 15,
        borderRadius: 25,
      },
      {
        label: "BC",
        data: labelsData.map((el: { bc_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.bc_sum : null
        ),
        backgroundColor: Colors.PASTEL_PINK,
        barThickness: 15,
        borderRadius: 25,
      },
      {
        label: "MISSING",
        data: labelsData.map((el: { missing_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.missing_sum : null
        ),
        backgroundColor: Colors.PASTEL_RED,
        barThickness: 15,
        borderRadius: 25,
      },
      {
        label: "DAMAGE",
        data: labelsData.map((el: { damage_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.damage_sum : null
        ),
        backgroundColor: Colors.PASTEL_VIOLET,
        barThickness: 15,
        borderRadius: 25,
      },
      {
        label: "FDD",
        data: labelsData.map((el: { fdd_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.fdd_sum : null
        ),
        backgroundColor: Colors.PASTEL_YELLOW,
        barThickness: 15,
        borderRadius: 25,
      },
      {
        label: "MISSORT",
        data: labelsData.map((el: { missort_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.missort_sum : null
        ),
        backgroundColor: Colors.PASTEL_TURQUOISE,
        barThickness: 15,
        borderRadius: 25,
      },
      {
        label: "OC",
        data: labelsData.map((el: { oc_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.oc_sum : null
        ),
        backgroundColor: Colors.PASTEL_SAND,
        barThickness: 15,
        borderRadius: 25,
      },
      {
        label: "OTHERS",
        data: labelsData.map((el: { others_sum: number; name: string }, index) =>
          el.name === labels[index] ? el.others_sum : null
        ),
        backgroundColor: Colors.PASTEL_PINE_TREE,
        barThickness: 15,
        borderRadius: 25,
      },
    ],
  };

  return (
    <Box className={classes.root}>
      {!externalSettingActive && (
        <Box className={classes.head}>
          <Box className={classes.box}>
            <StationSelect
              value={!withCompare ? compareStation : returnedDataByDriverSettings?.station}
              onChange={handleChangeStation}
            />
            <SearchTypeSelect
              value={selectType}
              onChange={onTypeChange}
              options={[
                { id: "week", title: "Week" },
                { id: "month", title: "Month" },
                { id: "day", title: "Day" },
              ]}
            />
            {selectType === "month" && (
              <MonthSelect
                month={month}
                year={year}
                onNext={onMonthIncrease}
                onPrev={onMonthReduce}
                disabled={dateConditionMonth()}
              />
            )}
            {selectType === "week" && (
              <WeekSelect
                week={week}
                year={year}
                onNext={onWeekIncrease}
                onPrev={onWeekReduce}
                disabled={dateCondition()}
              />
            )}
            {selectType === "day" && (
              <DatePickWithArrow
                value={date}
                onChange={onDateChange}
                changeDay={onDateChangeByDay}
                displayWeek
              />
            )}
            {loading && <CircularProgress />}
          </Box>
          <Box>
            {withCompare && (
              <ScorecardCompareButton
                children={<ReturnedDataByDriver withModal={false} withCompare={false} />}
                children2={<ReturnedDataByDriver withModal={false} withCompare={false} />}
              />
            )}
            {withModal && (
              <ScorecardOpenButton children={<ReturnedDataByDriver withModal={false} />} />
            )}
          </Box>
        </Box>
      )}
      <Typography variant="body2" style={{ width: "100%", textAlign: "center" }}>
        Returned Data By Driver
      </Typography>
      <Bar options={options} data={dataChart} />
    </Box>
  );
};

const options = {
  responsive: true,
  scale: {
    ticks: {
      precision: 0
    }
  },
  scales: {
    y: {
      stacked: true,
      title: {
        display: false,
        text: "Value",
      },
      min: 0,
      ticks: {
        color: `${Colors.MAIN}`,
        font: {
          weight: "700",
        },
      },
    },
    x: {
      stacked: true,
      ticks: {
        color: `${Colors.MAIN}`,
        font: {
          weight: "700",
        },
      },
    },
  },
  plugins: {
    legend: {
      position: "top" as const,
      display: false,
    },
    title: {
      display: false,
      text: "Returned Data By Driver",
    },
  },
};
